import React from 'react';
import ReactApexChart from "react-apexcharts";
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const SeverityDistributionChart = ({ severityStats, loading }) => {
  // Prepare chart data
  const getChartData = () => {
    if (!severityStats) {
      return { series: [], labels: [] };
    }

    const labels = [];
    const series = [];

    // Only include non-zero values
    if (severityStats.critical > 0) {
      labels.push('Critical (Level 12+)');
      series.push(severityStats.critical);
    }
    if (severityStats.high > 0) {
      labels.push('High (Level 7-11)');
      series.push(severityStats.high);
    }
    if (severityStats.medium > 0) {
      labels.push('Medium (Level 4-6)');
      series.push(severityStats.medium);
    }
    if (severityStats.low > 0) {
      labels.push('Low (Level 0-3)');
      series.push(severityStats.low);
    }

    return { series, labels };
  };

  const { series, labels } = getChartData();
  const hasData = series.length > 0 && series.some(val => val > 0);

  // Chart options
  const options = {
    chart: {
      height: 350,
      type: 'donut',
      toolbar: {
        show: false
      }
    },
    labels: labels,
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      style: {
        fontSize: '13px',
        fontWeight: 600,
      },
      dropShadow: {
        enabled: false
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontWeight: 600,
              color: ORANGE_COLORS.dark,
            },
            value: {
              show: true,
              fontSize: '24px',
              fontWeight: 700,
              color: ORANGE_COLORS.primary,
              formatter: (val) => parseInt(val).toLocaleString()
            },
            total: {
              show: true,
              label: 'Total Rules',
              fontSize: '14px',
              fontWeight: 600,
              color: '#6c757d',
              formatter: (w) => {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return total.toLocaleString();
              }
            }
          }
        },
      },
    },
    legend: {
      position: 'bottom',
      fontSize: '13px',
      fontWeight: 500,
      markers: { 
        width: 10, 
        height: 10, 
        radius: 8 
      },
      itemMargin: {
        horizontal: 12,
        vertical: 8
      }
    },
    colors: [
      ORANGE_COLORS.darker,   // Critical
      ORANGE_COLORS.dark,     // High
      ORANGE_COLORS.primary,  // Medium
      ORANGE_COLORS.light,    // Low
    ],
    tooltip: {
      y: { 
        formatter: (val) => `${parseInt(val).toLocaleString()} rules`
      },
      style: {
        fontSize: '13px',
      }
    },
    stroke: { 
      show: true,
      width: 2,
      colors: ['#fff']
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.1,
        }
      },
      active: {
        filter: {
          type: 'darken',
          value: 0.1,
        }
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card style={{ 
        background: "var(--vz-secondary-bg, #fff)",
        border: "1px solid var(--vz-border-color)",
        borderRadius: 10,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden"
      }}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
              background: toRgba(ORANGE_COLORS.primary, 0.10),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 8,
              flexShrink: 0
            }}>
              <i className="ri-pie-chart-line" style={{ color: ORANGE_COLORS.primary, fontSize: 18, lineHeight: 1 }}></i>
            </span>
            Rules Severity Distribution
          </h5>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  // No data state
  if (!hasData) {
    return (
      <Card style={{ 
        background: "var(--vz-secondary-bg, #fff)",
        border: "1px solid var(--vz-border-color)",
        borderRadius: 10,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden"
      }}>
        <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="card-title mb-0">
            <span style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
              background: toRgba(ORANGE_COLORS.primary, 0.10),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 8,
              flexShrink: 0
            }}>
              <i className="ri-pie-chart-line" style={{ color: ORANGE_COLORS.primary, fontSize: 18, lineHeight: 1 }}></i>
            </span>
            Rules Severity Distribution
          </h5>
        </CardHeader>
        <CardBody>
          <div className="text-center py-5">
            <i 
              className="ri-pie-chart-line display-4 mb-3 d-block" 
              style={{ color: ORANGE_COLORS.light }}
            ></i>
            <p className="text-muted">No rules data available</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Chart display
  return (
    <Card style={{ 
      background: "var(--vz-secondary-bg, #fff)",
      border: "1px solid var(--vz-border-color)",
      borderRadius: 10,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      overflow: "hidden"
    }}>
      <CardHeader style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
        <h5 className="card-title mb-0">
          <span style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
            background: toRgba(ORANGE_COLORS.primary, 0.10),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 8,
            flexShrink: 0
          }}>
            <i className="ri-pie-chart-line" style={{ color: ORANGE_COLORS.primary, fontSize: 12, lineHeight: 1 }}></i>
          </span>
          Rules Severity Distribution
        </h5>
      </CardHeader>
      <CardBody>
        <ReactApexChart 
          dir="ltr"
          className="apex-charts"
          series={series}
          options={options}
          type="donut"
          height={350}
        />
      </CardBody>
    </Card>
  );
};

export default SeverityDistributionChart;
